import { Lato, Poppins } from 'next/font/google';
import { Provider } from "react-redux";
// import { AuthGuard } from "../services/Auth/AuthGuard"; //adds on 40kb to build bundle size
import { useRouter } from "next/router";
import { LicenseInfo } from "@mui/x-license-pro";
import { wrapper } from "../js/Redux/store";
import dynamic from "next/dynamic";

import "../styles/globals.css";
import "../sass/app.scss";
import "../sass/global_classes.scss";
// import "../sass/_font.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import 'photoswipe/dist/photoswipe.css'

import { useMemo, useRef } from "react";
import Head from "next/head";
import Bugsnag from '@bugsnag/js'

const Error = dynamic(() => import("./_error"));
const AnalyticsScriptInjector = dynamic(() => import("@components/Custom/InjectAnalytics"), {ssr: false});

const poppins = Poppins({  weight: ['300','400', '700'], subsets: ['latin'] ,  style: ['normal'],}); 
const lato = Lato({  weight: ['100', '300','400','700', '900'], subsets: ['latin'] ,  style: ['normal'],}); 

const getFont = (fontFamily) => {
    switch (fontFamily) {
        case 'Poppins, sans-serif':
            return poppins;
        case 'Lato, sans-serif':
            return lato;
        default:
            return poppins;
    }
}

// Helper to split toe cookies
function parseCookies(cookieHeader = '') {
    return cookieHeader.split(';').reduce((acc, pair) => {
        const [key, ...rest] = pair.trim().split('=');
        if (!key) return acc;
        acc[key] = decodeURIComponent(rest.join('='));
        return acc;
    }, {});
}

if (!Bugsnag._client && process.env.NEXT_PUBLIC_APP_ENV == 'production' && !Bugsnag.isStarted()) {
    Bugsnag.start({
        apiKey: process.env.NEXT_PUBLIC_BUGSNAG_KEY ?? '',
        appType: "client",
        onError: (event) => {
            // Filter Playwright
            const msg = event.errors[0]?.errorMessage || ''
            if (/Service Worker registration blocked by Playwright/i.test(msg)) return false
        },
    })
}

LicenseInfo.setLicenseKey(
    process.env.NEXT_PUBLIC_MUI_KEY
);


const Layout = dynamic(() => import("../js/Custom/Layout"));


const ErrorBoundary = dynamic(() => import("./500"), {
    //lazy load to assist in performance
    ssr: true,
});

function MyApp({ Component, pageProps, globalProps, pathData, utm_data, ...rest }) {
    const { store } = wrapper.useWrappedStore(rest);
    const router = useRouter();
    const propsRef = useRef({ globalProps });
    const font = globalProps?.template_settings?.font_family || 'Poppins, sans-serif';
    const fontFamily = getFont(font);

    // Prefetching logic if necessary - review this part for correctness
    useMemo(() => {
        router.prefetch = async () => { }
    }, [router]);

    const initializeGlobalState = () => {
        if (store.getState().globalReducer.firstRender) {
            store.dispatch({
                type: 'GLOBAL_INIT_VARIABLES',
                payload: globalProps
            });

            const breadcrumbs = pageProps?.data?.breadcrumb;
            store.dispatch({
                type: "SET_GLOBAL_DATA",
                payload: {
                    // VATFlag: default to true/false based on your setting
                    breadcrumbs,
                    sidenav: pageProps?.data?.template_page_settings?.sidenav === 'yes' && globalProps?.template_settings?.sidenav === 'yes',
                    VATFlag: globalProps?.template_settings?.default_vat_toggle === 'inc_vat'
                }
            })

            store.dispatch({
                type: "USERJOURNEY_SET",
                payload: {
                    'utm-source': utm_data.utm_source,
                    'utm-medium': utm_data.utm_medium,
                    'utm-campaign': utm_data.utm_campaign,
                    'came-from': utm_data.came_from,
                }
            })
        }
    };

    const renderHead = () => {
        const meta = store.getState().globalReducer.template_page_content.home.meta;
        return meta ? (
            <Head>
                    <title>{meta.meta_title}</title>
                    <meta
                        name="description"
                        content={meta.meta_description}
                    />
                    <meta name="msapplication-TileColor" content="#da532c"/>
                    <meta name="theme-color" content="#ffffff"/>
                    <meta name="viewport" content="width=device-width, initial-scale=1.0,maximum-scale=1.0,user-scalable=no"/>
                    <meta charSet="utf-8"/>
            </Head>
        ) : null;
    };

    initializeGlobalState();

    if (pageProps?.error || globalProps?.error) {
        return <Error statusCode={pageProps?.statusCode || globalProps.statusCode} />;
    }

    return (
        <Provider store={store}>
            {renderHead()}
            <AnalyticsScriptInjector template_settings={globalProps?.template_settings || {}} />
            <main className={fontFamily.className}>
                <Layout 
                    {...pageProps}
                     _cookie={pathData?._cookie || false} 
                     utm_data={utm_data} 
                     breadcrumb={pageProps?.data?.breadcrumb}
                     pagePropsData={pageProps?.data}
                     font={fontFamily}
                >
                    <ErrorBoundary FallbackComponent={Error}>
                        <Component {...pageProps} />
                    </ErrorBoundary>
                </Layout>
            </main>
        </Provider>
    );
}


const parseUserAgent = (userAgent) => {
    if(typeof userAgent == 'undefined'){
        return {
            user_agent: null,
            isLine: null,
            isMobile: null,
            isInApp: null,
        }
    }
    
    const rules = [
        'WebView',
        '(iPhone|iPod|iPad)(?!.*Safari/)',
        'Android.*(wv|.0.0.0)'
    ];
    const regex = new RegExp(`(${rules.join('|')})`, 'ig')
 

    return {
        user_agent: userAgent,
        isLine: /\bLine\//i.test(userAgent),
        isMobile: /(iPhone|Android|Mobile)/i.test(userAgent),
        isInApp: Boolean(userAgent.match(regex)),
    };
}


function getUtmData(query = {}, req, res) {
    // Parse cookies
    const cookies = parseCookies(req?.headers?.cookie || '');

    // start with cookies
    const utm_data = {
        utm_source: cookies.utm_source || null,
        utm_medium: cookies.utm_medium || null,
        utm_campaign: cookies.utm_campaign || null,
        came_from: cookies.came_from || null,
    };

    // overwrite with query
    const queryData = {
        utm_source: query.utm_source || query['utm-source'],
        utm_medium: query.utm_medium || query['utm-medium'],
        utm_campaign: query.utm_campaign || query['utm-campaign'],
        came_from: query.camefrom || query['came-from'],
    };

    // Loop through keys and match them
    Object.keys(queryData).forEach((key) => {
        if (queryData[key]) utm_data[key] = queryData[key];
    });

    // set cookies utm data that is not null / empty value
    const newCookies = [];
    Object.entries(queryData).forEach(([key, value]) => {
        if (value) {
            newCookies.push(`${key}=${encodeURIComponent(value)}; Path=/; Max-Age=${60 * 60 * 2}`); //2 hours
        }
    });

    if (newCookies.length && res) {
        res.setHeader('Set-Cookie', newCookies);
    }
  
    return utm_data;
}

const getBasePath = (asPath) => {
    const path = asPath.match(new RegExp("/", "g")) || [];
    let basePath = asPath;
    if (path.length === 2) {
        basePath = basePath.substring(1);
        basePath = basePath.slice(0, basePath.indexOf("/"));
    }
    return basePath;
}

/**
 * Fetch some data server side before rendering the page client side.
 *
 * @param {object} context
 *   The context object.
 */
 MyApp.getInitialProps = async (appContext) => {
    const ctx = appContext.ctx;
    const {req, res, query, asPath} = ctx;
    const userAgent = req ? req.headers['user-agent'] : navigator.userAgent;
    const isServer = typeof window === 'undefined';

    const agent = parseUserAgent(userAgent);
    const utm_data = getUtmData(query, req, res);
    const basePath = getBasePath(asPath);
    const _cookie = req?.cookies?.['XSRF-TOKEN'] || false;

    if (isServer) {
        try {
            const dataReq = await fetch(`${process.env.NEXT_PUBLIC_BASE_API_URL}get-global-data`);
            const data = await dataReq.json();

            // const authenticator = new AuthGuard();
            // const user = await authenticator.authenticateUser(req, res, ctx.pathname, 'disabled', data.template_settings.portal_maintenance);
            const user = false;
            const propsToReturn = {
                globalProps: {
                        ...user,
                        ...data,
                        brand: process.env.brand,
                        agent: agent,
                },
                pathData: {
                    basePath: basePath,
                    pathname: ctx.pathname,
                    _cookie: _cookie
                },
                utm_data
            }

            return {...propsToReturn};
          
        } catch (error) {
            console.error(error);
            Bugsnag.notify(error)
            return {
                globalProps: {
                    error: true,
                    statusCode: 500
                }
            };
        }
    } else {
        // Client-side navigation
        return {
            pathData: { basePath, pathname: ctx.pathname, _cookie },
        };
    }
};

export default MyApp;
